import React, { useRef, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { TweenMax, TimelineMax, Power2 } from 'gsap';

/**
 * Typewriter-style animation: each character fades in and scales small → big → normal.
 */
function TypewriterText({ text, letterDelayMs = 35, className = '' }) {
    const containerRef = useRef(null);
    const [chars, setChars] = useState([]);

    useEffect(() => {
        if (!text) {
            setChars([]);
            return;
        }
        setChars(text.split(''));
    }, [text]);

    useEffect(() => {
        if (!containerRef.current || chars.length === 0) return;

        const spans = containerRef.current.querySelectorAll('.typewriter-char');
        if (spans.length === 0) return;

        const stagger = letterDelayMs / 1000;
        TweenMax.set(spans, { opacity: 0, scale: 0.3, display: 'inline-block' });

        const tl = new TimelineMax({ delay: 0.2 });
        spans.forEach((el, i) => {
            const t = i * stagger;
            tl.to(el, 0.25, { opacity: 1, scale: 1.2, ease: Power2.easeOut }, t)
                .to(el, 0.12, { scale: 1, ease: Power2.easeInOut }, t + 0.2);
        });

        return () => tl.kill();
    }, [chars, letterDelayMs]);

    if (!text) return null;

    return (
        <p className={className} ref={containerRef} aria-label={text}>
            {chars.map((char, i) => (
                <span key={`${i}-${char}`} className="typewriter-char">
                    {char === ' ' ? '\u00A0' : char}
                </span>
            ))}
        </p>
    );
}

TypewriterText.propTypes = {
    text: PropTypes.string.isRequired,
    letterDelayMs: PropTypes.number,
    className: PropTypes.string,
};

TypewriterText.defaultProps = {
    letterDelayMs: 35,
    className: '',
};

export default TypewriterText;
